import type { FC, ReactNode } from 'react'
import type { Movement, MovementType } from '@/types'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import UnitLabel from '@/components/inventory/unit-label'
import DateTime from '@/components/generals/date-time'
import { MOVEMENT_TYPE_META } from '@/constants/inventory'

interface RecentMovementsProps {
    movements: Movement[]
}

const ICON_STYLES: Record<MovementType, string> = {
    entrada: 'bg-flow-in-soft text-flow-in-ink',
    salida: 'bg-flow-out-soft text-flow-out-ink',
    ajuste: 'bg-flow-adjust-soft text-flow-adjust-ink'
}

const QUANTITY_STYLES: Record<MovementType, string> = {
    entrada: 'text-flow-in-ink',
    salida: 'text-flow-out-ink',
    ajuste: 'text-flow-adjust-ink'
}

const RecentMovements: FC<RecentMovementsProps> = ({ movements }): ReactNode => {
    const { signed } = useSettings()

    return (
        <ul className='-my-2.5 divide-y'>
            {movements.map(movement => {
                const Icon = MOVEMENT_ICONS[movement.type]

                return (
                    <li key={movement.id} className='flex items-start gap-3 py-2.5'>
                        <span
                            className={cn('mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full', ICON_STYLES[movement.type])}
                            title={MOVEMENT_TYPE_META[movement.type].label}
                        >
                            <Icon className='size-3.5' aria-hidden='true' />
                            <span className='sr-only'>{MOVEMENT_TYPE_META[movement.type].label}</span>
                        </span>

                        <div className='min-w-0 flex-1'>
                            <Link
                                href={`/inventario/${movement.productId}`}
                                className='block truncate text-sm font-medium text-foreground hover:text-primary hover:underline underline-offset-4'
                            >
                                <span data-mdlfy-cms={`products:${movement.productId}:name`}>{movement.productName}</span>
                            </Link>

                            <p className='mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground'>
                                {movement.reason && (
                                    <>
                                        <span className='truncate' data-mdlfy-cms={`stock_movements:${movement.id}:reason`}>{movement.reason}</span>
                                        <span aria-hidden='true'>·</span>
                                    </>
                                )}

                                <DateTime value={movement.createdAt} variant='relative' className='shrink-0' />
                            </p>
                        </div>

                        <span className={cn('shrink-0 text-sm font-semibold whitespace-nowrap tabular-nums', QUANTITY_STYLES[movement.type])}>
                            <span>{signed(movement.quantityChange)}</span> <UnitLabel productId={movement.productId} unit={movement.productUnit} className='text-xs font-normal' />
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}

export default RecentMovements
